import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useMacRelease } from '@/hooks/useMacRelease';
import { useT } from '@/i18n';
import { currentPlatform, IOS_SOON, MAC_DIRECT } from './appLinks';
import { BridgeAppIcon, CloseIcon, DownloadIcon, PhoneIcon } from './Icons';
import styles from './Layout.module.css';
import ui from './ui.module.css';

/**
 * The apps, as Slack, Notion and Claude offer theirs: one window, its main button for the visitor's own device
 * (“Download for Mac” downloads the file), and under it the iPhone and iPad apps, listed as coming. Elsewhere (Windows, Android) only that row.
 */
export function DownloadDialog({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const t = useT();
  const id = useId();
  const close = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const mine = currentPlatform();
  // the version on offer, once the appcast has answered; until then (and if it never does) the line has no version
  const release = useMacRelease();
  // over the middle of the app card rather than the middle of the screen — on a tall screen the card sits high —
  // while staying 16 px clear of the screen's edges; decided once, before the first paint
  useLayoutEffect(() => {
    const card = document.querySelector('[data-layout-root] main section');
    const el = box.current;
    if (!card || !el) return;
    const viewport = window.innerHeight;
    const rect = card.getBoundingClientRect();
    const wanted = (Math.max(rect.top, 0) + Math.min(rect.bottom, viewport)) / 2 - viewport / 2;
    const room = Math.max(0, (viewport - el.offsetHeight) / 2 - 16);
    const shift = Math.round(Math.max(-room, Math.min(room, wanted)));
    if (shift !== 0) el.style.translate = `0 ${String(shift)}px`;
  }, []);

  useEffect(() => {
    close.current?.focus();
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // rendered at the end of the page, not inside the header, so it sits over the whole page, centred on it
  return createPortal(
    <div
      className={ui.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={`${id}-text`} className={[ui.modal, styles.download].join(' ')} data-testid="download-dialog" ref={box}>
        <button ref={close} type="button" className={styles.downloadClose} aria-label={t('common.close')} onClick={onClose}>
          <CloseIcon size={16} />
        </button>
        <div className={ui.modalHead}>
          <BridgeAppIcon size={56} className={styles.downloadIcon} />
          <h2 id={`${id}-title`} className={styles.downloadTitle}>
            {t('downloadDialog.title')}
          </h2>
          <p id={`${id}-text`} className={styles.downloadText}>
            {t('downloadDialog.text')}
          </p>
        </div>
        {mine === null ? null : (
          <div className={ui.modalFoot}>
            <a className={[ui.btn, styles.downloadMain].join(' ')} href={MAC_DIRECT.href} download={MAC_DIRECT.file} data-testid="download-main">
              <DownloadIcon size={18} />
              {mine.action}
            </a>
            <p key={release?.version ?? 'plain'} className={[styles.downloadCaption, release === null ? '' : styles.downloadCaptionIn].join(' ')} data-testid="download-caption">
              {t('downloadDialog.caption', {
                file: release === null ? MAC_DIRECT.file : `${MAC_DIRECT.file} · ${release.version}`,
                requires: MAC_DIRECT.requires(release?.os ?? undefined),
              })}
            </p>
          </div>
        )}
        <ul className={[ui.modalBody, styles.downloadList].join(' ')} tabIndex={0} aria-label={t('downloadDialog.comingSoon')}>
          <li>
            <div className={styles.downloadRow} data-testid="download-soon">
              <span className={styles.downloadDevice} aria-hidden="true">
                <PhoneIcon size={20} />
              </span>
              <span className={styles.downloadName}>
                {IOS_SOON.name}
                <span className={styles.downloadRequires}>{IOS_SOON.requires}</span>
              </span>
              <span className={styles.downloadSoon}>{t('downloadDialog.soon')}</span>
            </div>
          </li>
        </ul>
      </div>
    </div>,
    document.body,
  );
}

